import React, { useEffect, useState } from "react";
import { useStore } from "react-redux";
import { fetchPatientByReference } from "../services/patientService";
import { fetchProviderByReference } from "../services/providerService";
import { fetchEncounterByReference } from "../services/encounterService";
import { fetchOrganizationByReference } from "../services/organizationService";
import { fetchSpecimenByReference } from "../services/specimenService";
import styles from "../modals/ResourceViewerModal.module.css";
import localStyles from "./ObservationCardExtended.module.css";
import { formatLocalDate } from "../utils/dateUtils";
import FemalePatient from "../assets/Patient_Female_96.png";
import MalePatient from "../assets/Patient_Male_96.png";
import FemaleProvider from "../assets/Doctor_Female.jpg";
import MaleProvider from "../assets/Doctor_Male_96.png";
import DefaultOrg from "../assets/world-health.png";
import { resolveName } from "../utils/messageUtils";
import { renderReference } from "../utils/renderUtils";

const SectionHeader = ({ children }) => (
    <div className={localStyles.sectionHeader}>{children}</div>
);

const Field = ({ label, children }) => (
    <div className="mb-1">
        <strong>{label}:</strong>{" "}
        {children || <span className="text-muted">N/A</span>}
    </div>
);

const renderCodeable = (cc) => {
    if (!cc) return <span className="text-muted">N/A</span>;

    if (cc.coding?.length) {
        return (
            <>
                {cc.coding.map((c, idx) => (
                    <div key={idx} className="text-muted small ps-3">
                        {c.system ? `${c.system} → ` : ""}
                        {c.code} — {c.display || c.code}
                    </div>
                ))}
            </>
        );
    }

    if (cc.text) return <div>{cc.text}</div>;
    return <span className="text-muted">N/A</span>;
};

const ServiceRequestCardExtended = ({ resource, handleShowNested, fhirClient }) => {
    const store = useStore();

    const [subject, setSubject] = useState(null);
    const [requester, setRequester] = useState(null);
    const [performers, setPerformers] = useState([]);
    const [organization, setOrganization] = useState(null);
    const [encounter, setEncounter] = useState(null);
    const [specimens, setSpecimens] = useState([]);

    useEffect(() => {
        if (!resource || !fhirClient) return;

        const fetchRefs = async () => {
            try {
                if (resource.subject?.reference) {
                    const s = await fetchPatientByReference(
                        resource.subject.reference,
                        fhirClient,
                        store
                    );
                    setSubject(s);
                }

                if (resource.requester?.reference) {
                    const r = await fetchProviderByReference(
                        resource.requester.reference,
                        fhirClient,
                        store
                    );
                    setRequester(r);
                }

                if (resource.encounter?.reference) {
                    const e = await fetchEncounterByReference(
                        resource.encounter.reference,
                        fhirClient,
                        store
                    );
                    setEncounter(e);
                }

                if (resource.performer?.length) {
                    const p = await Promise.all(
                        resource.performer.map((pr) =>
                            fetchProviderByReference(pr.reference, fhirClient, store)
                        )
                    );
                    setPerformers(p.filter(Boolean));
                }

                if (resource.specimen?.length) {
                    const s = await Promise.all(
                        resource.specimen.map((sp) =>
                            fetchSpecimenByReference(sp.reference, fhirClient, store)
                        )
                    );
                    setSpecimens(s.filter(Boolean));
                }

                if (resource.requester?.reference?.startsWith("Organization/")) {
                    const o = await fetchOrganizationByReference(
                        resource.requester.reference,
                        fhirClient,
                        store
                    );
                    setOrganization(o);
                }
            } catch (err) {
                console.error("ServiceRequestCardExtended: failed to fetch references", err);
            }
        };

        fetchRefs();
    }, [resource, fhirClient, store]);

    const patientName = resolveName(subject);
    const patientAvatar =
        subject?.photo?.[0]?.url ||
        (subject?.gender === "female" ? FemalePatient : MalePatient);

    const requesterName = resolveName(requester);
    const requesterAvatar =
        requester?.photo?.[0]?.url ||
        (requester?.gender === "female" ? FemaleProvider : MaleProvider);

    const orgName =
        resolveName(organization) ||
        (organization?.id ? `Organization/${organization.id}` : "Unknown Organization");

    return (
        <div className="p-4 border rounded shadow-sm bg-white">
            {/* HEADER */}
            <div className={localStyles.headerRow}>
                {/* Patient */}
                <div className={localStyles.entityBlock}>
                    <button
                        className={localStyles.entityImageButton}
                        onClick={() => handleShowNested?.(subject, patientName)}
                    >
                        <img src={patientAvatar} alt="Patient" className={localStyles.avatar} />
                    </button>
                    <div>
                        <Field label="Patient">{patientName}</Field>
                        <Field label="Birth Date">{subject?.birthDate}</Field>
                    </div>
                </div>

                {/* Requester */}
                <div className={localStyles.organizationBlock}>
                    <button
                        className={localStyles.entityImageButton}
                        onClick={() => handleShowNested?.(requester, requesterName)}
                    >
                        <img src={requesterAvatar} alt="Requester" className={localStyles.avatar} />
                    </button>
                    <div className={localStyles.orgText}>
                        <Field label="Requester">{requesterName}</Field>
                        <Field label="Active">
                            {requester ? (requester.active ? "Yes" : "No") : "N/A"}
                        </Field>
                    </div>

                    {organization && (
                        <>
                            <button
                                className={localStyles.entityImageButton}
                                onClick={() => handleShowNested?.(organization, orgName)}
                            >
                                <img src={DefaultOrg} alt="Organization" className={localStyles.avatar} />
                            </button>
                            <div className={localStyles.orgText}>
                                <div className={localStyles.orgName}>{orgName}</div>
                                <div className={localStyles.orgType}>
                                    {organization?.type?.text ||
                                        organization?.type?.coding?.[0]?.display}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* IDENTIFICATION */}
            <SectionHeader>Identification</SectionHeader>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <Field label="ServiceRequest ID">{resource.id}</Field>
                    <Field label="Status">{resource.status}</Field>
                    <Field label="Intent">{resource.intent}</Field>
                    <Field label="Priority">{resource.priority}</Field>
                    <Field label="Category">{renderCodeable(resource.category?.[0])}</Field>
                    <Field label="Code">{renderCodeable(resource.code)}</Field>
                </div>
            </div>

            {/* TIMING */}
            <SectionHeader>Timing</SectionHeader>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <Field label="Authored On">{formatLocalDate(resource.authoredOn)}</Field>
                    <Field label="Occurrence">
                        {formatLocalDate(resource.occurrenceDateTime)}
                    </Field>
                </div></div>

            {/* CONTEXT */}
            <SectionHeader>Context</SectionHeader>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <Field label="Encounter">
                        {encounter ? (
                            <button
                                className="btn btn-link p-0"
                                onClick={() => handleShowNested?.(encounter, resolveName(encounter))}
                            >
                                {resolveName(encounter)}
                            </button>
                        ) : "N/A"}
                    </Field>
                    <Field label="Reason Code">
                        {renderCodeable(resource.reasonCode?.[0])}
                    </Field>
                    <Field label="Reason Reference">
                        {resource.reasonReference?.length > 0 ? (
                            <div>
                                {resource.reasonReference.map((ref, idx) => (
                                    <div key={idx}>
                                        {renderReference(
                                            ref.reference || ref.display,
                                            ref.display || ref.reference,
                                            handleShowNested
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <span className="text-muted">None</span>
                        )}
                    </Field>
                </div></div>

            {/* SPECIMENS */}
            {specimens.length > 0 && (
                <>
                    <SectionHeader>Specimens</SectionHeader>
                    <div className={styles.indented}>
                        {specimens.map((s, idx) => (
                            <div key={idx} className="mb-2">
                                <button
                                    className="btn btn-link p-0"
                                    onClick={() =>
                                        handleShowNested?.(s, resolveName(s))
                                    }
                                >
                                    {resolveName(s) || `Specimen/${s.id}`}
                                </button>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* METADATA */}
            <SectionHeader>Metadata</SectionHeader>
            <div className={styles.indented}>
                <Field label="FHIR Resource Type">{resource.resourceType}</Field>
                <Field label="Version">{resource.meta?.versionId}</Field>
                <Field label="Last Updated">
                    {resource.meta?.lastUpdated
                        ? formatLocalDate(resource.meta.lastUpdated)
                        : "N/A"}
                </Field>
            </div>
        </div>
    );
};

export default ServiceRequestCardExtended;
